import { useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { LocalCanvas } from "./LocalCanvas";
import {
  createShapeId,
  createCommentThread,
  createComment,
  toRichText,
  DefaultFontStyle,
} from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import {
  MousePointer2,
  Pencil,
  Slash,
  Type,
  MessageSquare,
  Ruler,
  RotateCcw,
  Undo2,
  Redo2,
  Maximize,
  Columns2,
  ArrowLeft,
  Minus,
  Plus,
  Info,
} from "lucide-react";
import type { Sheet, Mark } from "../shared/schema";
import type { Room } from "./api";
import { installCanvasNavigation } from "./canvas-navigation";
import { installMouseButtonRecovery } from "./mouse-button-recovery";
import { installDraftingBehavior } from "./drafting-behavior";
import { installSheetSelection } from "./sheet-selection";
import { installSourceBehavior } from "./source-behavior";
import { useWorkspace } from "./useWorkspace";
import { CommentPins } from "./CommentPins";
import { DrawingToolbar } from "./DrawingToolbar";
import { SelectionToolbar } from "./SelectionToolbar";
import { SelectionStyle } from "./SelectionStyle";
import { WorkspaceDialogs } from "./WorkspaceDialogs";
import { LineAlignmentGuide } from "./LineAlignmentGuide";
import { LandingPlan } from "./LandingPlan";
import "./landing-workspace.css";

const room: Room = {
  id: "landing-demo",
  name: "Courtyard apartment",
  role: "owner",
  demo: true,
  created: "",
};
const user = { id: "landing-visitor", name: "You" };
const noAction = () => {};
const demoTools = [
  { id: "select", label: "Select", icon: MousePointer2 },
  { id: "draw", label: "Pen", icon: Pencil },
  { id: "line", label: "Line", icon: Slash },
  { id: "text", label: "Text", icon: Type },
  { id: "dimension", label: "Measure", icon: Ruler },
  { id: "pin", label: "Comment", icon: MessageSquare },
];

function planPreview(revised = false) {
  const svg = renderToStaticMarkup(
    <LandingPlan trace={revised} opacity={1} />,
  ).replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function fitDesk(editor: Editor) {
  editor.updateViewportScreenBounds(editor.getContainer());
  const bounds = editor.getCurrentPageBounds();
  if (bounds)
    editor.zoomToBounds(bounds, {
      inset: editor.getViewportScreenBounds().w < 600 ? 40 : 56,
    });
}

function seedDesk(editor: Editor) {
  const original = createShapeId("landing-original");
  const revised = createShapeId("landing-option");
  editor.createShapes<Sheet>([
    {
      id: original,
      type: "sheet",
      x: 0,
      y: 0,
      props: {
        w: 420,
        h: 480,
        name: "Existing plan",
        preview: planPreview(),
        metersPerUnit: 0.025,
        calibration: "Illustrative scale · 1 unit = 25 mm",
      },
    },
    {
      id: revised,
      type: "sheet",
      x: 470,
      y: 0,
      props: {
        w: 420,
        h: 480,
        name: "Option B · wider opening",
        preview: planPreview(true),
        metersPerUnit: 0.025,
        calibration: "Illustrative scale · 1 unit = 25 mm",
      },
    },
  ]);
  editor.createShape<Mark>({
    id: createShapeId("landing-dimension"),
    type: "mark",
    parentId: revised,
    x: 0,
    y: 0,
    props: {
      kind: "dimension",
      points: [
        { x: 55, y: 28 },
        { x: 365, y: 28 },
      ],
      color: "#202020",
      weight: 1,
    },
  });
  const thread = createCommentThread({
    pageId: editor.getCurrentPageId(),
    createdBy: "demo-marc",
    anchor: {
      type: "shape",
      shapeId: revised,
      x: 0.78,
      y: 0.43,
      isPrecise: true,
    },
  });
  editor.store.put([
    thread,
    createComment({
      pageId: editor.getCurrentPageId(),
      threadId: thread.id,
      authorId: "demo-marc",
      now: Date.now() - 120000,
      body: toRichText(
        "Could we widen this opening to bring more light into the living room?",
      ),
      meta: { author: "Marc" },
    }),
    createComment({
      pageId: editor.getCurrentPageId(),
      threadId: thread.id,
      authorId: "demo-emma",
      now: Date.now() - 60000,
      body: toRichText(
        "Option B widens the opening and keeps the room layout.",
      ),
      meta: { author: "Emma" },
    }),
  ]);
  editor.clearHistory();
  fitDesk(editor);
}

function DemoControls({
  editor,
  reset,
  comparing,
  setComparing,
}: {
  editor: Editor;
  reset: () => void;
  comparing: boolean;
  setComparing: (comparing: boolean) => void;
}) {
  const [busy, setBusy] = useState("");
  // Disconnected, non-cloud room: no sync, member queries or upload restoration.
  const model = useWorkspace({
    editor,
    room,
    user,
    connected: false,
    busy,
    setBusy,
    back: noAction,
  });
  const [reveal, setReveal] = useState(54);
  const [properties, setProperties] = useState<string | null>(null);
  const compareButton = useRef<HTMLButtonElement>(null);
  const slider = useRef<HTMLInputElement>(null);
  const threadBeforeCompare = useRef<string | null>(null);
  const infoMenu = useRef<HTMLDetailsElement>(null);
  const selectionKey = model.selected.map((shape) => shape.id).join(",");
  const exampleThread = model.threads.find((thread) => !thread.resolved)?.id;
  useEffect(() => {
    editor.cancel();
    editor.blur();
    model.setPending(null);
    model.setThreadId(exampleThread ?? null);
  }, [editor, exampleThread, model.setPending, model.setThreadId]);
  useEffect(() => setProperties(null), [selectionKey]);
  useEffect(() => {
    const update = () => {
      editor.updateViewportScreenBounds(editor.getContainer());
      const bounds = editor.getViewportScreenBounds();
      if (bounds.maxY <= 0 || bounds.y >= window.innerHeight) {
        editor.blur();
      }
    };
    window.addEventListener("scroll", update, true);
    return () => window.removeEventListener("scroll", update, true);
  }, [editor]);
  useEffect(() => {
    if (!comparing) return;
    slider.current?.focus({ preventScroll: true });
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setComparing(false);
      model.setThreadId(threadBeforeCompare.current);
      compareButton.current?.focus({ preventScroll: true });
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [comparing, setComparing, model.setThreadId]);
  const toggleCompare = () => {
    editor.cancel();
    editor.blur();
    model.setPending(null);
    if (comparing) {
      model.setThreadId(threadBeforeCompare.current);
    } else {
      threadBeforeCompare.current = model.threadId;
      model.setThreadId(null);
    }
    setComparing(!comparing);
  };
  const drawingTool = ["draw", "line", "dimension"].includes(model.tool);
  return (
    <>
      <header className="landing-workspace-header">
        <span className="landing-project-name">
          Courtyard apartment <span>Example project</span>
        </span>
        <div>
          <button
            ref={compareButton}
            className={comparing ? "is-active" : ""}
            onClick={toggleCompare}
            aria-expanded={comparing}
            aria-controls="landing-comparison"
          >
            <Columns2 size={16} />
            <span>Compare plans</span>
          </button>
          <details className="landing-demo-info" ref={infoMenu}>
            <summary
              aria-label="About this demo"
              title="About this demo"
              onKeyDown={(event) => {
                if (event.key === "Escape" && infoMenu.current) {
                  infoMenu.current.open = false;
                  event.currentTarget.focus();
                }
              }}
            >
              <Info size={16} />
            </summary>
            <p>Changes stay in this demo and reset when you reload.</p>
          </details>
          <button onClick={reset} aria-label="Reset demo" title="Reset demo">
            <RotateCcw size={16} />
          </button>
        </div>
      </header>
      {comparing ? (
        <section
          id="landing-comparison"
          className="landing-comparison"
          aria-label="Compare the original and revised plans"
        >
          <div className="landing-compare-work">
            <div
              className="landing-compare-plan"
              role="img"
              aria-label={`Plan comparison: ${reveal}% revised plan. The revised plan widens the courtyard opening.`}
            >
              <LandingPlan />
              <div
                className="landing-compare-revision"
                style={{ clipPath: `inset(0 ${100 - reveal}% 0 0)` }}
              >
                <LandingPlan trace opacity={1} />
              </div>
              <div
                className="landing-compare-seam"
                style={{ left: `${reveal}%` }}
                aria-hidden="true"
              >
                <span>↔</span>
              </div>
            </div>
            <label className="landing-compare-slider">
              <span>Slide to compare</span>
              <input
                ref={slider}
                type="range"
                min="0"
                max="100"
                value={reveal}
                aria-label="Reveal revised plan"
                aria-valuetext={`${reveal}% revised plan`}
                onChange={(event) => setReveal(Number(event.target.value))}
              />
              <span className="landing-compare-labels">
                <span>Existing plan</span>
                <span>Wider opening</span>
              </span>
            </label>
          </div>
          <aside className="landing-compare-notes">
            <p className="entry-eyebrow">Compare drawings</p>
            <h3>
              Test the opening.
              <br />
              Keep the original.
            </h3>
            <p>
              Slide between the existing plan and wider glazing. The kitchen,
              dining and living spaces stay in place.
            </p>
            <blockquote>
              <span>Marc · on the drawing</span>“Could we widen this opening to
              bring more light into the living room?”
            </blockquote>
            <button
              onClick={() => {
                setComparing(false);
                model.setThreadId(threadBeforeCompare.current);
                compareButton.current?.focus();
              }}
            >
              <ArrowLeft size={16} /> Back to the workspace
            </button>
          </aside>
        </section>
      ) : (
        <>
          <div
            className="landing-demo-toolbar"
            role="toolbar"
            aria-label="Demo drawing tools"
          >
            {demoTools.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                aria-label={label}
                aria-pressed={model.tool === id}
                title={label}
                onClick={() => model.choose(id)}
              >
                <Icon size={18} />
                <span>{label}</span>
              </button>
            ))}
          </div>
          <div className="landing-demo-history">
            <button
              aria-label="Undo"
              title="Undo"
              onClick={() => editor.undo()}
            >
              <Undo2 size={16} />
            </button>
            <button
              aria-label="Redo"
              title="Redo"
              onClick={() => editor.redo()}
            >
              <Redo2 size={16} />
            </button>
          </div>
          <div className="landing-demo-zoom">
            <button
              aria-label="Zoom out"
              title="Zoom out"
              onClick={() => editor.zoomOut()}
            >
              <Minus size={16} />
            </button>
            <button
              aria-label="Fit drawings"
              title="Fit drawings"
              onClick={() => fitDesk(editor)}
            >
              <Maximize size={16} />
            </button>
            <button
              aria-label="Zoom in"
              title="Zoom in"
              onClick={() => editor.zoomIn()}
            >
              <Plus size={16} />
            </button>
          </div>
          <SelectionToolbar
            model={model}
            onProperties={(key) =>
              setProperties(properties === key ? null : key)
            }
          />
          {drawingTool && !model.pending && (
            <DrawingToolbar key={model.tool} model={model} />
          )}
          {!drawingTool &&
            properties === selectionKey &&
            model.selected.length > 0 && (
              <SelectionStyle
                model={model}
                onClose={() => setProperties(null)}
              />
            )}
          <LineAlignmentGuide editor={editor} />
          <aside
            className="landing-workspace-context"
            aria-label="Materials and discussion"
          >
            <figure className="landing-material-reference">
              <figcaption>Material reference</figcaption>
              <img
                src="/landing/story/05-interior-inspiration.webp"
                alt="Pale oak, travertine and linen beside a darker walnut material palette"
                width={1536}
                height={1024}
              />
            </figure>
            <CommentPins
              model={model}
              allowCopyLink={false}
              clipToViewport
              dockThread
            />
            {!model.threadId && !model.pending && exampleThread && (
              <button
                className="landing-open-discussion"
                onClick={() => model.setThreadId(exampleThread)}
              >
                <MessageSquare size={16} /> View discussion
              </button>
            )}
          </aside>
          {(model.cal || model.scaleRequest) && (
            <WorkspaceDialogs model={model} />
          )}
        </>
      )}
    </>
  );
}

function DemoCanvas({
  reset,
  comparing,
  setComparing,
}: {
  reset: () => void;
  comparing: boolean;
  setComparing: (comparing: boolean) => void;
}) {
  // No persistenceKey, remote store, uploads or customer board data.
  const [editor, setEditor] = useState<Editor | null>(null);
  return (
    <div
      className={`board landing-workspace${comparing ? " is-comparing" : ""}`}
      id="landing-demo-panel"
      role="region"
      aria-label="Interactive apartment demo"
    >
      <div className="canvas-area">
        <LocalCanvas
          onMount={(ed) => {
            const disposers = [
              installCanvasNavigation(ed),
              installMouseButtonRecovery(ed.getContainer()),
              installSourceBehavior(ed),
              installDraftingBehavior(ed),
              installSheetSelection(ed),
            ];
            ed.setStyleForNextShapes(DefaultFontStyle, "sans");
            ed.updateInstanceState({ isGridMode: false });
            if (!ed.getCurrentPageShapes().length) seedDesk(ed);
            // Mobile starts with the marked alternative; Fit reveals both drawings.
            if (ed.getViewportScreenBounds().w < 600) {
              const bounds = ed.getShapePageBounds(
                createShapeId("landing-option"),
              );
              if (bounds) ed.zoomToBounds(bounds, { inset: 48 });
            }
            setEditor(ed);
            return () => disposers.forEach((dispose) => dispose());
          }}
        />
      </div>
      {editor && (
        <DemoControls
          editor={editor}
          reset={reset}
          comparing={comparing}
          setComparing={setComparing}
        />
      )}
    </div>
  );
}

export function LandingWorkspace() {
  const [version, setVersion] = useState(0);
  const [comparing, setComparing] = useState(false);
  return (
    <DemoCanvas
      key={version}
      comparing={comparing}
      setComparing={setComparing}
      reset={() => {
        setComparing(false);
        setVersion((value) => value + 1);
      }}
    />
  );
}
